import React from 'react'
import { Link } from 'react-router-dom'
import './_savedOverview.scss'

// Overview shared by /student/notes and /student/bookmarks: a header with the
// total, the three content types with counts, subjects with counts, then the
// student's tags as chips. Navigation stays with the caller: onType(count,
// path) and onSubject(subjectRow) are the sections' existing handlers (they
// show the "nothing saved yet" alert when the count is 0).

const SUBJECT_TONES = {
    botany: "#10b981",
    zoology: "#f59e0b",
    physics: "#3b82f6",
    chemistry: "#8b5cf6",
    mathematics: "#ef4444",
    maths: "#ef4444",
};

const subjectTone = (name) => SUBJECT_TONES[String(name || "").trim().toLowerCase()] || "#64748b";

const SavedOverview = ({ kind, counts, subjects, tags, basePath, onType, onSubject, defaultActiveKey }) => {
    const noun = kind === "notes" ? "Notes" : "Bookmarks";
    const types = [
        { key: "short", label: "Short notes & revision material", icon: "fa-clipboard", tone: "#10b981", count: counts.short_notes_count, path: `${basePath}/shortnotes-and-materials` },
        { key: "practice", label: "Practice questions", icon: "fa-pencil-alt", tone: "#f59e0b", count: counts.practice_questions_count, path: `${basePath}/practice-questions` },
        { key: "exam", label: "Exam questions", icon: "fa-file-alt", tone: "#ef4444", count: counts.exam_questions_count, path: `${basePath}/exam-questions` },
    ];
    const total = types.reduce((sum, t) => sum + (parseInt(t.count, 10) || 0), 0);

    return (
        <div className="sv">
            <div className="sv-head">
                <div>
                    <h1 className="sv-title">My {noun}</h1>
                    <p className="sv-sub">
                        {kind === "notes"
                            ? "Everything you've written notes on, by type, subject and tag."
                            : "Everything you've bookmarked, by type, subject and tag."}
                    </p>
                </div>
                <div className="sv-total"><strong>{total}</strong> saved</div>
            </div>

            <section className="sv-section">
                <h2 className="sv-label">By type</h2>
                <div className="sv-grid sv-grid--types">
                    {types.map((t) => (
                        <button
                            key={t.key}
                            type="button"
                            className="sv-card"
                            style={{ "--tone": t.tone }}
                            onClick={(e) => onType(e, t.count, t.path)}>
                            <span className="sv-card-icon"><i className={"fas " + t.icon} /></span>
                            <span className="sv-card-text">
                                <span className="sv-card-name">{t.label}</span>
                                <span className="sv-card-count">{t.count || 0}</span>
                            </span>
                            <i className="fas fa-chevron-right sv-card-arrow" />
                        </button>
                    ))}
                </div>
            </section>

            {subjects && subjects.length > 0 ? (
                <section className="sv-section">
                    <h2 className="sv-label">By subject</h2>
                    <div className="sv-grid sv-grid--subjects">
                        {subjects.map((s) => (
                            <button
                                key={s.id || s.subject_name}
                                type="button"
                                className="sv-subject"
                                style={{ "--tone": subjectTone(s.subject_name) }}
                                onClick={() => onSubject(s)}>
                                <span className="sv-subject-name">{s.subject_name}</span>
                                <span className="sv-subject-count">{s.count || 0}</span>
                            </button>
                        ))}
                    </div>
                </section>
            ) : null}

            <section className="sv-section">
                <h2 className="sv-label">Your tags</h2>
                {tags.length > 0 ? (
                    <div className="sv-tags">
                        {tags.map((t) => (
                            <Link
                                key={t.id}
                                className="sv-tag"
                                to={{
                                    pathname: `${basePath}/shortnotes-and-materials`,
                                    state: {
                                        tagid: t.id,
                                        subjectid: 0,
                                        ...(defaultActiveKey ? { defaultActiveKey } : {}),
                                    },
                                }}>
                                <i className="fas fa-tag" />
                                {t.tag}
                                <span className="sv-tag-count">{t.count}</span>
                            </Link>
                        ))}
                    </div>
                ) : (
                    <div className="sv-empty">
                        No tags yet. Tags you add while saving {kind === "notes" ? "a note" : "a bookmark"} will show here.
                    </div>
                )}
            </section>
        </div>
    );
};

export default SavedOverview;
